/**
 * @file Élément individuel de notification in-app dans le drawer de notifications.
 * @module common.layouts._common.notifications-popover.NotificationItem
 *
 * Affiche une notification avec son icône thématique, le message narratif (support **bold**)
 * et la date relative. Toutes les actions (navigation et toggle lu/non lu) sont accessibles
 * via un menu 3 points visible au survol, évitant tout conflit de propagation d'événements.
 *
 * Le mapping type → route est assuré côté frontend (paths.js) : le backend ne stocke pas d'URL.
 */
import PropTypes from 'prop-types';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {Icon} from '@iconify/react';
import {paths} from 'packages/common/routes/paths';
import {fToNow} from 'packages/common/utils/formatTime';

const BLUE = {
  color: '#1976d2',
  bg: 'rgba(25, 118, 210, 0.08)',
  border: 'rgba(25, 118, 210, 0.18)',
  unreadBg: 'rgba(25, 118, 210, 0.04)',
  unreadHover: 'rgba(25, 118, 210, 0.07)',
};

const TYPE_CONFIG = {
  restaurant: {...BLUE, icon: 'solar:calendar-bold'},
  gift_voucher: {...BLUE, icon: 'solar:gift-bold'},
  invoice: {...BLUE, icon: 'mdi:receipt-text'},
  sms: {...BLUE, icon: 'solar:chat-round-bold'},
  default: {...BLUE, icon: 'mdi:notifications-none'},
};

/**
 * Retourne la configuration visuelle selon le type de notification
 *
 * @param {string} type - Type de notification (restaurant_*, gift_voucher_*, invoice_*)
 * @return {Object} Configuration visuelle (icône, couleur, fond)
 */
function getConfigForType(type) {
  if (type?.startsWith('restaurant_')) return TYPE_CONFIG.restaurant;
  if (type?.startsWith('gift_voucher_')) return TYPE_CONFIG.gift_voucher;
  if (type?.startsWith('invoice_')) return TYPE_CONFIG.invoice;
  if (type?.startsWith('sms_')) return TYPE_CONFIG.sms;
  return TYPE_CONFIG.default;
}

/**
 * Dérive l'URL et le libellé de navigation à partir des données de la notification
 *
 * Le backend ne stocke pas d'URL : c'est le frontend qui est seul responsable
 * du mapping type → route, via paths.js comme source de vérité.
 *
 * @param {Object} notification - Données de la notification
 * @return {Object} URL et libellé contextuel ({ link: string|null, label: string|null })
 */
function getNavigationInfo({type, entityId, metadata}) {
  if (type === 'restaurant_customer_message') {
    return {
      link: entityId ? paths.app.restaurant.conversation(entityId) : paths.app.restaurant.messages,
      label: 'Voir la discussion',
    };
  }

  if (type?.startsWith('restaurant_')) {
    const base = paths.app.restaurant.reservations;
    return {
      link: metadata?.date ? `${base}?date=${metadata.date}` : base,
      label: 'Voir la réservation',
    };
  }

  if (type === 'gift_voucher_daily_report' || type === 'gift_voucher_weekly_report') {
    return {link: paths.app.giftVoucher.statistics, label: 'Voir les statistiques'};
  }

  if (type === 'gift_voucher_b2b_sale') {
    return {link: paths.app.settings.payment.invoices, label: 'Voir la facture'};
  }

  if (type?.startsWith('gift_voucher_') && entityId) {
    return {link: paths.app.giftVoucher.details(entityId), label: 'Voir le bon cadeau'};
  }

  if (type?.startsWith('invoice_')) {
    return {link: paths.app.settings.payment.invoices, label: 'Voir la facture'};
  }

  if (type === 'sms_recharge_invoice') {
    return {link: paths.app.smsWallet, label: 'Recharger mon solde SMS'};
  }

  if (type === 'sms_low_balance') {
    return {link: paths.app.smsWallet, label: 'Recharger mon solde SMS'};
  }

  return {link: null, label: null};
}


/**
 * Parse un texte contenant des marqueurs **bold** et retourne un tableau de nœuds React
 * avec les portions entre ** rendues en <strong>.
 *
 * Rétrocompatible : un texte sans marqueur est retourné tel quel.
 *
 * @param {string} text - Texte brut avec éventuels marqueurs **bold**
 * @return {Array} Tableau de strings et éléments <strong>
 */
function renderRichText(text) {
  if (!text) return null;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i} style={{fontWeight: 600, color: 'inherit'}}>{part}</strong> : part,
  );
}


/**
 * Élément de notification in-app
 *
 * Affiche une notification avec son icône thématique, le message narratif et la date relative.
 * Toutes les actions (navigation et toggle lu/non lu) sont accessibles via le menu 3 points
 * qui apparaît au survol, évitant tout conflit de propagation d'événements.
 *
 * @param {Object} props
 * @param {Object} props.notification - Données de la notification
 * @param {Function} [props.onClose] - Callback pour fermer le drawer parent
 * @param {Function} [props.onToggleRead] - Callback (notificationId, currentIsRead) pour basculer l'état lu
 * @return {JSX.Element}
 */
export default function NotificationItem({notification, onClose, onToggleRead}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const navigate = useNavigate();

  const config = getConfigForType(notification.type);
  const isUnread = !notification.isRead;
  const {link, label: navLabel} = getNavigationInfo(notification);

  const handleOpenMenu = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleCloseMenu = () => setMenuAnchor(null);

  const handleNavigate = () => {
    handleCloseMenu();
    onClose?.();
    navigate(link);
  };

  const handleToggleRead = () => {
    handleCloseMenu();
    onToggleRead?.(notification._id, notification.isRead);
  };

  return (
    <Box
      sx={{
        'px': 2.5,
        'py': 1.75,
        'display': 'flex',
        'gap': 2,
        'alignItems': 'flex-start',
        'position': 'relative',
        'bgcolor': isUnread ? config.unreadBg : 'transparent',
        'transition': 'background-color 0.15s ease',
        '&:hover': {
          'bgcolor': isUnread ? config.unreadHover : 'action.hover',
          '& .notif-menu-btn': {opacity: 1},
        },
        '&:not(:last-child)': {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >

      {/* Icône thématique avec dot non-lu */}
      <Box sx={{position: 'relative', flexShrink: 0, mt: 0.25}}>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: config.bg,
            border: `1.5px solid ${config.border}`,
          }}
        >
          <Icon icon={config.icon} width={16} style={{color: config.color}} />
        </Stack>

        {isUnread && (
          <Box
            sx={{
              position: 'absolute',
              top: -3,
              right: -3,
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: config.color,
              border: '2px solid white',
            }}
          />
        )}
      </Box>

      <Box sx={{flex: 1, minWidth: 0, pr: 2.5}}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 400,
            color: 'text.primary',
            lineHeight: 1.6,
            mb: 0.75,
          }}
        >
          {renderRichText(notification.message || notification.title)}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Icon icon="solar:clock-circle-linear" width={11} style={{color: '#bdbdbd'}} />
          <Typography sx={{color: 'text.disabled', fontSize: '0.75rem'}}>
            {(() => {
              const t = fToNow(notification.createdAt);
              return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
            })()}
          </Typography>
        </Stack>
      </Box>

      {/* Bouton 3 points — visible uniquement au survol de l'item */}
      <IconButton
        className="notif-menu-btn"
        size="small"
        onClick={handleOpenMenu}
        sx={{
          'position': 'absolute',
          'top': 10,
          'right': 8,
          'opacity': 0,
          'transition': 'opacity 0.15s ease',
          'p': 0.5,
          'color': 'text.disabled',
          '&:hover': {color: 'text.secondary', bgcolor: 'action.hover'},
        }}
      >
        <Icon icon="eva:more-vertical-fill" width={16} />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {minWidth: 210, borderRadius: 1.5, boxShadow: '0 4px 20px rgba(0,0,0,0.12)'},
        }}
        transformOrigin={{horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
      >
        {link && (
          <MenuItem onClick={handleNavigate} dense sx={{gap: 1.5, py: 1}}>
            <Icon icon="eva:diagonal-arrow-right-up-fill" width={16} style={{color: '#637381'}} />
            <Typography variant="body2">{navLabel}</Typography>
          </MenuItem>
        )}

        {link && <Divider sx={{my: 0.5}} />}

        <MenuItem onClick={handleToggleRead} dense sx={{gap: 1.5, py: 1}}>
          <Icon
            icon={isUnread ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
            width={16}
            style={{color: '#637381'}}
          />
          <Typography variant="body2">
            {isUnread ? 'Marquer comme lu' : 'Marquer comme non lu'}
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
    entityId: PropTypes.string,
    metadata: PropTypes.object,
    isRead: PropTypes.bool,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }),
  onClose: PropTypes.func,
  onToggleRead: PropTypes.func,
};
